/**
 * StepComplete.tsx — Onboarding success page with confetti.
 */

import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import SecurityIcon from '@mui/icons-material/Security';

interface Props {
  plan: string;
  riskScore: number;
  findingCount: number;
  connectedProvider?: string;
  sessionId: string;
  accessToken: string;
  trialDays?: number;
}

// ── Canvas confetti ────────────────────────────────────────────────────────────
function useConfetti(canvasRef: React.RefObject<HTMLCanvasElement>) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width  = window.innerWidth;
    canvas.height = window.innerHeight;

    const COLORS = ['#00b4d8', '#f77f00', '#2ecc71', '#ef233c', '#f4d03f', '#a855f7'];
    const pieces = Array.from({ length: 120 }, () => ({
      x:  Math.random() * canvas.width,
      y:  Math.random() * -canvas.height,
      w:  6 + Math.random() * 8,
      h:  10 + Math.random() * 14,
      r:  Math.random() * Math.PI * 2,
      dr: (Math.random() - 0.5) * 0.15,
      dy: 2 + Math.random() * 4,
      dx: (Math.random() - 0.5) * 1.5,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
    }));

    let raf: number;
    let running = true;

    function draw() {
      if (!running || !ctx || !canvas) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      pieces.forEach((p) => {
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.r);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = 0.85;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
        p.y  += p.dy;
        p.x  += p.dx;
        p.r  += p.dr;
        if (p.y > canvas.height) { p.y = -20; p.x = Math.random() * canvas.width; }
      });
      raf = requestAnimationFrame(draw);
    }

    draw();
    // Stop after 4 seconds
    const stop = setTimeout(() => { running = false; cancelAnimationFrame(raf); ctx.clearRect(0, 0, canvas.width, canvas.height); }, 4000);
    return () => { running = false; cancelAnimationFrame(raf); clearTimeout(stop); };
  }, []); // eslint-disable-line
}

export default function StepComplete({ plan, riskScore, findingCount, connectedProvider, sessionId, accessToken, trialDays = 14 }: Props) {
  const navigate   = useNavigate();
  const canvasRef  = useRef<HTMLCanvasElement>(null);
  useConfetti(canvasRef);

  function goToDashboard() {
    // Store the token so AuthContext picks it up
    localStorage.setItem('shield_access_token', accessToken);
    navigate('/dashboard', { replace: true });
  }

  const planLabel = plan.charAt(0).toUpperCase() + plan.slice(1);
  const providerLabel: Record<string, string> = { aws: 'AWS', gcp: 'Google Cloud', azure: 'Microsoft Azure', ssh: 'Private Server' };
  const scoreLabel = riskScore <= 25 ? 'Low Risk' : riskScore <= 50 ? 'Medium Risk' : riskScore <= 75 ? 'High Risk' : 'Critical';

  return (
    <>
      <canvas ref={canvasRef} style={{ position: 'fixed', top: 0, left: 0, pointerEvents: 'none', zIndex: 9999 }} />

      <Box maxWidth={520} mx="auto" textAlign="center" sx={{ position: 'relative' }}>
        <IconButton
          onClick={() => navigate('/')}
          sx={{ position: 'absolute', top: 16, right: 16, color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}
        >
          <CloseIcon />
        </IconButton>
        <Typography variant="h2" mb={1}>🎉</Typography>
        <Typography variant="h4" fontWeight={800} mb={1}>Welcome to Shield!</Typography>
        <Typography variant="body1" color="text.secondary" mb={4}>
          Your security is now being monitored 24/7. Here's what we set up:
        </Typography>

        <Stack spacing={1.5} sx={{ mb: 4, textAlign: 'left' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, borderRadius: 2,
            bgcolor: 'rgba(46,204,113,0.08)', border: '1px solid rgba(46,204,113,0.2)' }}>
            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 28 }} />
            <Box>
              <Typography variant="body2" fontWeight={600}>{planLabel} Plan Activated</Typography>
              <Typography variant="caption" color="text.secondary">{trialDays}-day free trial started today</Typography>
            </Box>
            <Chip label={planLabel} size="small" color="primary" sx={{ ml: 'auto' }} />
          </Box>

          {connectedProvider && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, borderRadius: 2,
              bgcolor: 'rgba(46,204,113,0.08)', border: '1px solid rgba(46,204,113,0.2)' }}>
              <CheckCircleIcon sx={{ color: 'success.main', fontSize: 28 }} />
              <Box>
                <Typography variant="body2" fontWeight={600}>Cloud Connected</Typography>
                <Typography variant="caption" color="text.secondary">{providerLabel[connectedProvider] || connectedProvider}</Typography>
              </Box>
              <Chip label="Connected" size="small" color="success" sx={{ ml: 'auto' }} />
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, borderRadius: 2,
            bgcolor: 'rgba(0,180,216,0.08)', border: '1px solid rgba(0,180,216,0.2)' }}>
            <SecurityIcon sx={{ color: 'primary.main', fontSize: 28 }} />
            <Box>
              <Typography variant="body2" fontWeight={600}>First Scan Complete</Typography>
              <Typography variant="caption" color="text.secondary">
                Found {findingCount} issue{findingCount !== 1 ? 's' : ''} — Risk Score: {riskScore}/100 ({scoreLabel}, higher = more risk)
              </Typography>
            </Box>
          </Box>
        </Stack>

        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={goToDashboard}
          sx={{ fontWeight: 700, py: 1.8, fontSize: 16, mb: 2 }}
        >
          Go to My Dashboard →
        </Button>

        <Typography variant="caption" color="text.disabled">
          No credit card required during trial. Cancel any time.
        </Typography>
      </Box>
    </>
  );
}
